import React, { useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
    downloadExcel_HRM2040_R01, getHRM2040_R01List,
    patchHRM2040_R01, postHRM2040_R01Aggregate,
} from "./Api_HRM2040_R01";
import { HRM2040_R01Dto } from "./Dto_HRM2040_R01";
import { HRM2040_R01Filter, useHRM2040_R01Store } from "./Store_HRM2040_R01";
import { formatYearMonthWithHyphen } from "src/utils/date/dateUtils";
import { NUMBER_COLUMN_FIELDS } from "./Table_HRM2040_R01";

/** 월근태 편집 그리드와 화면 메시지 변경 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2040_R01Dto> | null>; // 편집을 확정할 그리드 참조
    setMessage: (message: string) => void; // 조회·저장 결과를 하단에 표시한다.
}

/** 조회 완료 안내를 표시할지 지정한다. */
type FetchOptions = {
    messageMode?: 'show' | 'none'; // none이면 저장·재집계 후 완료 안내를 숨긴다.
};

/**
 * 변경 행의 필수값·중복·숫자를 서버 규칙과 같은 순서로 검사한다.
 * @param list PATCH로 보낼 변경 행
 * @param gridRef 컬럼 제목을 읽을 편집 그리드
 * @returns 첫 오류 문장. 모두 유효하면 빈 문자열
 */
const validateHRM2040_R01SaveData = (
    list: HRM2040_R01Dto[],
    gridRef: React.RefObject<AgGridReact<HRM2040_R01Dto> | null>,
): string => {
    /** 같은 요청에 사원 한 명이 두 번 포함되었는지 확인할 식별값이다. */
    const seenEmployees = new Set<string>();
    for (const row of list) {
        if (!row.empNo || !row.workYm) return '사원과 근무년월이 필요합니다.';
        // 정규식: 연도 네 자리 뒤에 01~12월 두 자리만 허용한다.
        if (!/^\d{4}(0[1-9]|1[0-2])$/.test(row.workYm)) return '근무년월은 YYYYMM 형식이어야 합니다.';
        /** 서버 오류 문장에 넣을 사원 이름이며 이름이 없으면 번호를 쓴다. */
        const employeeName = row.empNm || row.empNo;
        /** 요청 안에서 동일 사원·연월을 구분하는 값이다. */
        const employeeKey = `${row.empNo}:${row.workYm}`;
        if (seenEmployees.has(employeeKey)) return `${employeeName}: 같은 사원이 두 번 입력되었습니다.`;
        seenEmployees.add(employeeKey);
        for (const field of NUMBER_COLUMN_FIELDS) {
            /** null·NaN·음수 여부를 확인할 현재 집계 칸 값이다. */
            const value = row[field];
            if (!Number.isFinite(value) || value < 0) {
                // 오류 문장의 제목도 Table의 실제 컬럼 정의에서 읽는다.
                const headerName = gridRef.current?.api.getColumn(field)?.getColDef().headerName ?? field;
                return `${employeeName}: ${headerName}은(는) 0 이상이어야 합니다.`;
            }
        }
        if (row.remark && row.remark.length > 1000) {
            return `${employeeName}: 비고는 1000자 이하여야 합니다.`;
        }
    }
    return '';
};

/**
 * 월근태 조회·저장·재집계·Excel·초기화 동작을 화면 요소에 연결한다.
 * @param gridRef 편집 확정과 조회 후 스크롤 이동에 사용할 그리드
 * @param setMessage 하단 화면 메시지 변경 함수
 * @returns Container가 버튼과 Table에 전달할 상태·동작 함수
 */
export const HRM2040_R01Content = ({ gridRef, setMessage }: Props) => {
    // 화면 Store의 검색·행·원본·스크롤 상태 변경 함수를 가져온다.
    const {
        setHRM2040_R01List, setOriginalHRM2040_R01List,
        setHRM2040_R01PastFilter, setScrollTop, initData,
    } = useHRM2040_R01Store();
    // 공통 확인창의 내용과 표시 상태를 갱신할 함수를 가져온다.
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    // API 요청 중 로딩 표시를 켜고 끄는 함수를 가져온다.
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // true이면 행 클릭으로 선택한다. 공통 그리드 호환을 위해 Table에 전달한다.
    const [clickable, setClickable] = useState(true);

    // 이 화면의 액션바 권한을 서버 설정에 따라 적용한다.
    useFetchActionbarAuth('HRM2040_R01');

    /**
     * 빈 사업장·부서 조건은 쿼리 파라미터에서 제외한다.
     * @param filter 현재 또는 직전 조회조건
     * @returns 서버 GET 요청에 넣을 근무년월·사업장·부서
     */
    const getParams = useCallback((filter: HRM2040_R01Filter): Partial<HRM2040_R01Filter> => ({
        workYm: filter.workYm,
        compCd: filter.compCd || undefined, // 빈 사업장은 전체 조건이므로 전송하지 않는다.
        orgCd: filter.orgCd || undefined, // 빈 부서는 전체 조건이므로 전송하지 않는다.
    }), []);

    /** 저장하지 않은 숫자 또는 비고 변경 행이 있으면 true이다. */
    const hasChanges = useCallback((): boolean => {
        return useHRM2040_R01Store.getState().HRM2040_R01List.some(row => row.isModified);
    }, []);

    /** 공통 확인창을 닫고 이전 확인창 내용을 비운다. */
    const closeConfirmModal = useCallback(() => {
        setConfirmModalVisible(false);
        setConfirmModalProps(undefined);
    }, [setConfirmModalVisible, setConfirmModalProps]);

    /**
     * 저장하지 않은 변경이 있으면 버릴지 묻고 확인한 경우에만 다음 동작을 실행한다.
     * @param onConfirm 변경이 없거나 버리기 확인 후 실행할 함수
     * @returns 반환값은 없다.
     */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) {
            onConfirm();
            return;
        }
        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                closeConfirmModal();
                onConfirm();
            },
            onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, closeConfirmModal, setConfirmModalProps, setConfirmModalVisible]);

    /**
     * 목록을 조회하고 성공했을 때만 목록·원본·선택·직전 조건을 갱신한다.
     * @param fetchType search는 현재 조건, refetch는 성공한 직전 조회조건을 사용한다.
     * @param options 조회 완료·빈 목록 안내 표시 여부
     * @returns 조회 요청과 상태 갱신이 끝나는 Promise
     */
    const executeDataFetch = useCallback(async (
        fetchType: 'search' | 'refetch', options?: FetchOptions,
    ) => {
        /** 버튼 클릭 이후의 최신 검색조건과 직전 성공 조건이다. */
        const state = useHRM2040_R01Store.getState();
        /** 재조회는 변경 중인 현재 조건 대신 직전 조회조건을 사용한다. */
        const filter = fetchType === 'refetch' ? state.HRM2040_R01PastFilter : state.HRM2040_R01Filter;
        if (!filter) {
            return;
        }
        setLoadingSpinner(true);
        try {
            const response = await getHRM2040_R01List(fetchType, getParams(filter), 0);
            // 실패 응답은 공통 인터셉터가 알리므로 목록을 보존한다.
            if (!response.data.successFlag) {
                return;
            }
            /** 화면과 원본에 동일하게 저장할 서버 행 목록이다. */
            const list = response.data.data;
            if (fetchType === 'search') setScrollTop(null);
            setHRM2040_R01List(list);
            setOriginalHRM2040_R01List(list);
            useHRM2040_R01Store.getState().initSelectedHRM2040_R01List();
            setHRM2040_R01PastFilter(filter);
            if (list.length > 0) {
                gridRef.current?.api.ensureIndexVisible(0, 'top');
                if (options?.messageMode !== 'none') {
                    ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${list.length}건)`);
                }
            } else if (options?.messageMode !== 'none') {
                showWarning('집계된 자료가 없습니다. 월근태 재집계를 실행하세요.');
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [getParams, gridRef, setLoadingSpinner, setScrollTop, setHRM2040_R01List,
        setOriginalHRM2040_R01List, setHRM2040_R01PastFilter, setMessage]);

    /** 조회 전에 편집을 확정하고 변경이 있으면 버리기 확인창을 띄운다. */
    const fetchData = useCallback(() => {
        gridRef.current?.api.stopEditing();
        confirmDiscard(() => {
            void executeDataFetch('search');
        });
    }, [gridRef, confirmDiscard, executeDataFetch]);

    /**
     * 변경된 기존 행만 검증해 PATCH로 저장하고 성공하면 직전 조건으로 재조회한다.
     * @returns 저장 요청과 재조회가 끝나는 Promise
     */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        // 셀 편집 이벤트가 갱신한 최신 Store 목록을 읽는다.
        /** isModified가 true인 기존 월근태 행만 저장한다. */
        const modifiedList = useHRM2040_R01Store.getState().HRM2040_R01List
            .filter(row => row.isModified);
        if (modifiedList.length === 0) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        /** 백엔드 검증과 동일한 첫 오류 문장이다. */
        const error = validateHRM2040_R01SaveData(modifiedList, gridRef);
        if (error) {
            showWarning(error);
            return;
        }
        setLoadingSpinner(true);
        try {
            const response = await patchHRM2040_R01(modifiedList);
            if (!response.data.successFlag) {
                return;
            }
            showSuccess('월근태 저장이 완료되었습니다.');
            setMessage('월근태 저장이 완료되었습니다.');
            await executeDataFetch('refetch', { messageMode: 'none' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, setLoadingSpinner, setMessage, executeDataFetch]);

    /**
     * 직전 조회조건의 서버 XLSX를 내려받는다.
     * @returns 다운로드 요청이 끝나는 Promise
     */
    const handleExcelClick = useCallback(async () => {
        /** 조회 성공 이후에만 존재하는 Excel 요청 조건이다. */
        const filter = useHRM2040_R01Store.getState().HRM2040_R01PastFilter;
        if (!filter) {
            showWarning('먼저 조회하세요.');
            return;
        }
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM2040_R01('search', getParams(filter), 0);
            if (!response.data.successFlag) {
                return;
            }
            downloadSettingExcel({
                responseData: response.data.data,
                fileName: `월근태 집계_${formatYearMonthWithHyphen(filter.workYm)}-excel.xlsx`,
            });
        } finally {
            setLoadingSpinner(false);
        }
    }, [getParams, setLoadingSpinner]);

    /**
     * 확인된 직전 조회조건으로 서버에서 집계하고 같은 조건의 목록을 다시 읽는다.
     * @param filter 마지막으로 성공한 조회의 근무년월·사업장·부서
     * @returns 재집계와 재조회가 끝나는 Promise
     */
    const executeHRM2040_R01Aggregate = useCallback(async (filter: HRM2040_R01Filter) => {
        setLoadingSpinner(true);
        try {
            const response = await postHRM2040_R01Aggregate(filter);
            if (!response.data.successFlag) {
                return;
            }
            /** 서버가 실제로 집계한 재직 사원 수이다. */
            const count = response.data.data;
            if (count === 0) {
                showWarning('집계할 재직 사원이 없습니다.');
            } else {
                showSuccess(`${formatYearMonthWithHyphen(filter.workYm)} 일일근태 ${count}명분을 다시 집계했습니다.`);
            }
            await executeDataFetch('refetch', { messageMode: 'none' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, executeDataFetch]);

    /**
     * 직전 조회조건에 맞는 사원만 서버에서 다시 집계한다. 변경 중인 행은 먼저 저장하게 한다.
     * @returns 반환값은 없다. 확인창 승인 뒤 서버 요청을 시작한다.
     */
    const handleAggregateClick = useCallback(() => {
        gridRef.current?.api.stopEditing();
        /** 재집계에 사용할 마지막 성공 조회의 근무년월·사업장·부서이다. */
        const filter = useHRM2040_R01Store.getState().HRM2040_R01PastFilter;
        if (!filter) {
            showWarning('먼저 조회하세요.');
            return;
        }
        if (hasChanges()) {
            showWarning('변경한 내용을 먼저 저장한 뒤 재집계하세요.');
            return;
        }
        setConfirmModalProps({
            title: '월근태 재집계',
            message: `${formatYearMonthWithHyphen(filter.workYm)} 일일근태로 월근태를 다시 집계합니다. 숫자 항목은 일일근태 기준으로 바뀌고 비고는 유지됩니다.`,
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                closeConfirmModal();
                void executeHRM2040_R01Aggregate(filter);
            },
            onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [gridRef, hasChanges, executeHRM2040_R01Aggregate,
        setConfirmModalProps, setConfirmModalVisible, closeConfirmModal]);

    // Container에서 액션바·검색 영역·그리드에 연결할 값만 노출한다.
    return {
        fetchData, executeDataFetch, handleSaveClick, handleExcelClick,
        handleAggregateClick, initData, clickable, setClickable,
    };
};
